'use client';

import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { z } from 'zod';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import IntroStep from '../shared/IntroStep';
import Step1System from './Step1System';
import Step2Contact from './Step2Contact';
import StepIndicator from './StepIndicator';
import ConsumerSuccessPage from './ConsumerSuccessPage';
import FeedbackConfirmation from './FeedbackConfirmation';
import { submitQuoteAction } from '@/app/actions/quote'; // Server Action
import { CheckCircle, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import AltchaWidget, { AltchaErrorDetail } from '@/components/forms/AltchaWidget';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { E2E_BYPASS_KEY, E2E_BYPASS_TOKEN, isE2EBypass } from '@/lib/e2e-bypass';
import { trackEvent } from '@/lib/analytics';
import { clientLogger } from '@/lib/client-logger';
import { serializeError } from '@/lib/serialize-error';

import { quoteSchema, QuoteFormSchema } from './schema';

const FORM_NAME = 'particulier_offerte';

interface QuoteFormProps {
	showLogo?: boolean;
	showToggle?: boolean;
	introContent?: string;
	showIntro?: boolean;
	showForm?: boolean;
}

function QuoteFormContent({
	showLogo = true,
	showToggle = true,
	introContent,
	showIntro = true,
	showForm = true,
}: QuoteFormProps) {
	const searchParams = useSearchParams();
	const router = useRouter();
	const pathname = usePathname();

	// Derived state from URL, default to 1
	const currentStep = Number(searchParams.get('step')) || 1;

	// E2E bypass: skip ALTCHA when ?_e2e=BREUER_E2E_2024
	const e2eBypass = isE2EBypass(searchParams.get(E2E_BYPASS_KEY));

	const [showIntroScreen, setShowIntroScreen] = useState(showIntro && !!introContent);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [altchaPayload, setAltchaPayload] = useState<string | null>(e2eBypass ? E2E_BYPASS_TOKEN : null);
	const [leadId, setLeadId] = useState<string | undefined>();
	const [contactName, setContactName] = useState<string | undefined>();
	const formRef = useRef<HTMLDivElement>(null);
	const previousStep = useRef(currentStep);
	const previousShowIntro = useRef(showIntroScreen);

	const methods = useForm<QuoteFormSchema>({
		resolver: zodResolver(quoteSchema),
		defaultValues: {
			system_surface_area: undefined,
			system_isolation_level: undefined,
			location_remarks: '',
			location_street: '',
			location_house_number: '',
			location_postal_code: '',
			location_city: '',
			contact_name: '',
			contact_email: '',
			contact_phone: '',
			privacy_agreed: false,
		},
		mode: 'onSubmit',
	});

	const { trigger, handleSubmit } = methods;

	const scrollToFormTop = () => {
		if (formRef.current) {
			const rect = formRef.current.getBoundingClientRect();
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			window.scrollTo({
				top: rect.top + scrollTop - 100, // 100px offset for breathing room/sticky nav
				behavior: 'smooth',
			});
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	// Scroll to the top of the form on step change
	useEffect(() => {
		if (previousStep.current === currentStep) {
			return;
		}
		previousStep.current = currentStep;
		scrollToFormTop();
	}, [currentStep]);

	// Scroll to the top of the form when leaving the intro screen
	useEffect(() => {
		if (previousShowIntro.current === showIntroScreen) {
			return;
		}
		previousShowIntro.current = showIntroScreen;
		scrollToFormTop();
	}, [showIntroScreen]);

	// Track Form Steps in Analytics
	useEffect(() => {
		if (!showForm || showIntroScreen) return;
		trackEvent('form_step_view', {
			form_name: 'particulier_offerte',
			step: currentStep,
		});
	}, [currentStep, showIntroScreen, showForm]);

	useEffect(() => {
		if (!showForm || !showIntroScreen) return;
		trackEvent('form_intro_view', { form_name: 'particulier_offerte' });
	}, [showIntroScreen, showForm]);

	const continueFromIntro = () => {
		trackEvent('form_intro_continue', { form_name: 'particulier_offerte' });
		setShowIntroScreen(false);
	};

	const updateStep = (step: number) => {
		const params = new URLSearchParams(searchParams);
		params.set('step', step.toString());
		router.push(`${pathname}?${params.toString()}`, { scroll: false });
	};

	const nextStep = async () => {
		let valid = false;
		if (currentStep === 1) {
			valid = await trigger(['system_surface_area', 'system_isolation_level', 'location_photo']);
		}

		if (valid) {
			updateStep(currentStep + 1);
		}
	};

	const skipToStep2 = () => {
		trackEvent('form_skip_step1', { form_name: 'particulier_offerte' });
		updateStep(2);
	};

	const prevStep = () => {
		updateStep(Math.max(currentStep - 1, 1));
	};

	const handleAltchaError = useCallback((detail: AltchaErrorDetail) => {
		clientLogger.error('ALTCHA widget error', {
			stage: detail.stage,
			form: FORM_NAME,
			...serializeError(detail.error),
		});
	}, []);

	const onSubmit = async (data: QuoteFormSchema) => {
		const requestId = crypto.randomUUID();

		if (!altchaPayload && !e2eBypass) {
			clientLogger.warn('Form submit blocked: ALTCHA not ready', { form: FORM_NAME, request_id: requestId });
			setError('Beveiliging wordt geladen, probeer het over een moment opnieuw.');

			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const formData = new FormData();

			// Append ALTCHA token
			formData.append('altcha', altchaPayload || E2E_BYPASS_TOKEN);
			formData.append('_request_id', requestId);

			// E2E bypass flag for server-side
			if (e2eBypass) {
				formData.append('_e2e_bypass', 'true');
			}

			// Device metadata for lead enrichment (parsed server-side)
			formData.append('_device_ua', navigator.userAgent || '');
			formData.append('_device_screen', `${window.innerWidth}x${window.innerHeight}`);
			formData.append('_device_type', window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop');
			formData.append('_device_language', navigator.language || 'unknown');

			// Append fields
			Object.entries(data).forEach(([key, value]) => {
				if (key === 'location_photo' && Array.isArray(value)) {
					value.forEach((file) => {
						if (file instanceof File) {
							formData.append('location_photo', file);
						}
					});
				} else if (value !== undefined && value !== null) {
					formData.append(key, String(value));
				}
			});

			// Submit with a safety timeout to prevent infinite hanging on mobile
			const result = await Promise.race([
				submitQuoteAction({ success: false }, formData),
				new Promise<never>((_resolve, reject) =>
					setTimeout(() => reject(new Error('SUBMIT_TIMEOUT')), 30_000)
				),
			]);

			if (result.success) {
				trackEvent('form_submit_success', {
					form_name: 'particulier_offerte',
				});
				setLeadId(result.leadId);
				setContactName(methods.getValues('contact_name'));
				updateStep(3);
			} else {
				trackEvent('form_submit_error', {
					form_name: 'particulier_offerte',
					error_type: 'server_validation',
				});
				setError(result.message || 'Er is iets misgegaan. Probeer het opnieuw.');
				// Reset ALTCHA for retry
				setAltchaPayload(null);
			}
		} catch (err) {
			const isTimeout = err instanceof Error && err.message === 'SUBMIT_TIMEOUT';
			clientLogger.error('Quote form submission failed', {
				error_type: isTimeout ? 'timeout' : 'exception',
				form: FORM_NAME,
				request_id: requestId,
				...serializeError(err),
			});
			trackEvent('form_submit_error', {
				form_name: 'particulier_offerte',
				error_type: isTimeout ? 'timeout' : 'catch_error',
			});
			setError(
				isTimeout
					? 'Het verzenden duurt te lang. Controleer uw internetverbinding en probeer het opnieuw.'
					: 'Er is iets misgegaan. Probeer het later opnieuw of neem telefonisch contact op.',
			);
			// Reset ALTCHA for retry
			setAltchaPayload(null);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (currentStep === 3) {
		return <ConsumerSuccessPage leadId={leadId} contactName={contactName} onFeedbackSent={() => updateStep(4)} />;
	}

	if (currentStep === 4) {
		return <FeedbackConfirmation />;
	}

	return (
		<div ref={formRef} className="w-full bg-gray-50">
			{/* Header - Full Width */}
			{/* Header - Full Width */}
			<div className="w-full bg-blue-dark py-4 md:py-6 lg:py-8">
				<div className="max-w-6xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-[auto_1fr_auto] items-center gap-4 lg:gap-6">
					{/* Toggle Switch (Mobile: Top Right | Desktop: Right) */}
					{showToggle && (
						<div className="col-start-2 row-start-1 lg:col-start-3 lg:row-start-1 justify-self-end flex-shrink-0 flex items-center p-1 rounded-full border border-white/20">
							<a
								href="/offerte"
								className="px-3 sm:px-4 lg:px-6 py-1.5 lg:py-2 rounded-full text-xs sm:text-sm lg:text-base font-bold transition-all duration-200 bg-white text-blue-dark shadow-sm cursor-default"
							>
								Particulier
							</a>
							<a
								href="/zakelijk-offerte"
								className="px-3 sm:px-4 lg:px-6 py-1.5 lg:py-2 rounded-full text-xs sm:text-sm lg:text-base font-medium transition-all duration-200 text-white/90 hover:text-white hover:bg-white/10"
								onClick={() => trackEvent('offerte_toggle_click', { from: 'particulier', to: 'zakelijk' })}
							>
								Zakelijk
							</a>
						</div>
					)}

					{/* Logo (Mobile: Top Left | Desktop: Left) */}
					{showLogo && (
						<a
							href="/"
							className="col-start-1 row-start-1 justify-self-start flex-shrink-0 hover:opacity-90 transition-opacity"
						>
							<img
								src="/images/breuerklimaattechniek-logo-white.png"
								alt="Breuer Klimaattechniek"
								className="h-6 sm:h-8 lg:h-12 w-auto object-contain"
							/>
						</a>
					)}

					{/* Text block (Mobile: Bottom | Desktop: Center) */}
					<div className="col-span-2 row-start-2 lg:col-span-1 lg:col-start-2 lg:row-start-1 text-left lg:text-center mt-1 lg:mt-0">
						<h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-white">Offerte Aanvragen</h2>
						<p className="text-blue-100 mt-1 text-sm sm:text-base lg:text-lg font-medium">
							Binnen enkele werkdagen een reactie van onze experts
						</p>
					</div>
				</div>
			</div>

			{!showForm ? (
				showIntro && introContent ? <IntroStep content={introContent} showContinue={false} /> : null
			) : showIntroScreen ? (
				<IntroStep content={introContent ?? ''} onContinue={continueFromIntro} />
			) : (
				<>
					{/* Progress - Full Width */}
					<div className="w-full bg-gray-50 border-b border-gray-200">
						<div className="max-w-3xl mx-auto">
							<StepIndicator currentStep={currentStep} totalSteps={2} />
						</div>
					</div>

					{/* Form - Centered */}
					<div className="max-w-2xl mx-auto px-4 py-8 md:py-12">
						<FormProvider {...methods}>
							<form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-8">
								<AnimatePresence mode="wait">
									{currentStep === 1 && (
										<motion.div
											key="step1"
											initial={{ opacity: 0, x: 20 }}
											animate={{ opacity: 1, x: 0 }}
											exit={{ opacity: 0, x: -20 }}
											transition={{ duration: 0.3 }}
										>
											<Step1System />
										</motion.div>
									)}
									{currentStep === 2 && (
										<motion.div
											key="step2"
											initial={{ opacity: 0, x: 20 }}
											animate={{ opacity: 1, x: 0 }}
											exit={{ opacity: 0, x: -20 }}
											transition={{ duration: 0.3 }}
										>
											<Step2Contact />
										</motion.div>
									)}
								</AnimatePresence>

								{/* ALTCHA Widget (invisible, Web Worker-based PoW) */}
								{!e2eBypass && <AltchaWidget onVerified={setAltchaPayload} onError={handleAltchaError} />}

								{error && (
									<div className="p-4 bg-red-50 text-red-600 rounded-md text-sm font-medium">{error}</div>
								)}

								{/* Navigation */}
								<div className="flex justify-between items-center pt-8 mt-8 border-t border-gray-200">
									{currentStep > 1 ? (
										<Button
											type="button"
											variant="ghost"
											onClick={prevStep}
											disabled={isSubmitting}
											className="text-gray-600 hover:text-blue-dark"
										>
											<ArrowLeft className="mr-2 size-4" /> Vorige
										</Button>
									) : (
										<div></div> // Spacer
									)}

									{currentStep < 2 ? (
										<div className="flex items-center gap-3">
											<button
												type="button"
												onClick={skipToStep2}
												className="text-sm text-gray-400 hover:text-blue-dark transition-colors underline underline-offset-2"
											>
												Sla over
											</button>
											<Button
												type="button"
												onClick={nextStep}
												className="bg-blue-dark hover:bg-blue text-white font-bold px-8"
											>
												Volgende <ArrowRight className="ml-2 size-4" />
											</Button>
										</div>
									) : (
										<Button
											type="submit"
											className="bg-orange hover:bg-orange-hover text-white w-full md:w-auto min-w-[200px] font-bold text-lg py-6"
											disabled={isSubmitting}
										>
											{isSubmitting ? (
												<>
													<Loader2 className="mr-2 size-5 animate-spin" /> Bezig met verzenden...
												</>
											) : (
												'Vraag Offerte Aan'
											)}
										</Button>
									)}
								</div>
							</form>
						</FormProvider>
					</div>
				</>
			)}
		</div>
	);
}

export default function QuoteForm(props: QuoteFormProps) {
	return (
		<Suspense
			fallback={
				<div className="min-h-[600px] flex items-center justify-center">
					<Loader2 className="size-8 animate-spin text-blue-dark" />
				</div>
			}
		>
			<QuoteFormContent {...props} />
		</Suspense>
	);
}
